import { createAtom, useSelector } from '@tanstack/react-store';
import { WebFeatureFlagNames, WebFeatureFlags } from '@web/launchDarkly/flags';
import { useFlags as useLdFlags } from 'launchdarkly-react-client-sdk';

const getEnvFlagValue = <K extends WebFeatureFlagNames>(key: K): WebFeatureFlags[K] | undefined => {
  const rawValue = import.meta.env[`VITE_APP_FF_${key}`];

  if (rawValue === undefined) {
    return;
  }

  try {
    // oxlint-disable-next-line typescript/consistent-return typescript/no-unsafe-return -- OK
    return JSON.parse(rawValue);
  } catch {
    // Do nothing
  }
};

export const envFeatureFlags = Object.keys(import.meta.env)
  .filter((key) => key.startsWith('VITE_APP_FF_'))
  .reduce<Partial<WebFeatureFlags>>((rest, key) => {
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- OK
    const flagName = key.replace('VITE_APP_FF_', '') as WebFeatureFlagNames;

    Object.assign(rest, {
      [flagName]: getEnvFlagValue(flagName),
    });
    return rest;
  }, {});

export const supportsChangingOrigin = import.meta.env.VITE_APP_SUPPORT_CHANGING_ORIGIN === 'true';

let currentLocalFeatureFlags: Partial<WebFeatureFlags> = {};

export const featureFlagsAtom = createAtom<Partial<WebFeatureFlags>>(currentLocalFeatureFlags);

export function getLocalFeatureFlags(): Partial<WebFeatureFlags> {
  return { ...currentLocalFeatureFlags };
}

export function replaceLocalFeatureFlags(featureFlags: Partial<WebFeatureFlags>) {
  currentLocalFeatureFlags = { ...featureFlags };
  featureFlagsAtom.set(currentLocalFeatureFlags);
}

export function updateLocalFeatureFlags(featureFlags: Partial<WebFeatureFlags>) {
  replaceLocalFeatureFlags({
    ...currentLocalFeatureFlags,
    ...featureFlags,
  });
}

export function resetLocalFeatureFlag(flagName: WebFeatureFlagNames) {
  const { [flagName]: _removed, ...rest } = currentLocalFeatureFlags;
  replaceLocalFeatureFlags(rest);
}

export function useLocalFeatureFlags() {
  return useSelector(featureFlagsAtom, (state) => state);
}

export function useFlag<K extends WebFeatureFlagNames>(flagName: K): WebFeatureFlags[K] | undefined {
  const flags = useLdFlags<WebFeatureFlags>();
  const localFlagValue = useSelector(featureFlagsAtom, (state) => state?.[flagName]);
  const envFlagValue = envFeatureFlags[flagName];

  const ldFlag = flags[flagName];

  if (supportsChangingOrigin) {
    return localFlagValue ?? envFlagValue ?? ldFlag;
  }

  return ldFlag;
}

export function useRemoteFlags() {
  return useLdFlags<WebFeatureFlags>();
}
